import { formatoCantidad, formatoPorcentaje, formatoPrecio } from '@/lib/utils/formato-precio';
import type { MercadoDelRecorrido } from './recorrido-del-precio';

/**
 * «En el mercado» en palabras (spec 225, pieza 2; lote B1, maqueta `b1-fila`).
 * Puro: el componente sólo pinta lo que sale de acá.
 *
 * - Una fila por tienda que cuenta, de la más barata a la más cara, con su
 *   precio, hace cuánto se leyó y si con el precio que queda quedás arriba,
 *   abajo o igual.
 * - Una lectura de más de 30 días (confirmado por Sebastián el 28-sep) es vieja:
 *   va en naranja y el bloque lo avisa.
 * - Tu tienda va aparte, en gris: no cuenta.
 */

/** Más de esto, la lectura es vieja (spec 225, pregunta 1, 28-sep). */
export const DIAS_DE_LECTURA_VIEJA = 30;
const DIA_MS = 24 * 60 * 60 * 1000;
/** Menos de 0,05 % (5 centavos cada 100) es «igual». */
const CASI_IGUAL_PCT = 0.05;

/**
 * «hoy», «ayer», «hace 12 días», «hace 2 meses». Sin fecha, no se sabe. Con
 * `alProponer` (`ahora` es cuándo se congeló): «el mismo día», «1 día antes»,
 * «12 días antes», «2 meses antes».
 */
export function haceCuantoSeLeyo(
  iso: string | null,
  ahora: number = Date.now(),
  alProponer = false
): string {
  if (!iso) return 'no se sabe cuándo';
  const dias = Math.max(0, Math.floor((ahora - new Date(iso).getTime()) / DIA_MS));
  const meses = Math.floor(dias / 30);
  if (alProponer) {
    if (dias === 0) return 'el mismo día';
    if (dias === 1) return '1 día antes';
    if (dias < 60) return `${formatoCantidad(dias)} días antes`;
    return meses === 1 ? '1 mes antes' : `${formatoCantidad(meses)} meses antes`;
  }
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 60) return `hace ${formatoCantidad(dias)} días`;
  return meses === 1 ? 'hace 1 mes' : `hace ${formatoCantidad(meses)} meses`;
}

export function esLecturaVieja(iso: string | null, ahora: number = Date.now()): boolean {
  return iso != null && (ahora - new Date(iso).getTime()) / DIA_MS > DIAS_DE_LECTURA_VIEJA;
}

/** Con el precio que queda, frente a una tienda: «quedás 29,6 % abajo», «quedás igual». */
export function frenteA(queda: number, precio: number): string {
  if (precio <= 0) return '—';
  const pct = ((queda - precio) / precio) * 100;
  if (Math.abs(pct) < CASI_IGUAL_PCT) return 'quedás igual';
  return pct < 0
    ? `quedás ${formatoPorcentaje(Math.abs(pct))} abajo`
    : `quedás ${formatoPorcentaje(pct)} arriba`;
}

/** «queda 5.364,99: 29,6 % debajo del más barato» / «… igual al más barato». */
function cabecera(queda: number, masBarato: number): string {
  const pct = ((queda - masBarato) / masBarato) * 100;
  const donde =
    Math.abs(pct) < CASI_IGUAL_PCT
      ? 'igual al más barato'
      : pct < 0
        ? `${formatoPorcentaje(Math.abs(pct))} debajo del más barato`
        : `${formatoPorcentaje(pct)} sobre el más barato`;
  return `queda ${formatoPrecio(queda)}: ${donde}`;
}

export interface LecturaDelMercado {
  /** null sin precio que comparar (sin precio posible) o sin tiendas. */
  cabecera: string | null;
  tiendas: Array<{
    nombre: string;
    precio: number;
    hace: string;
    vieja: boolean;
    /** null sin precio que comparar. */
    frente: string | null;
    laMasBarata: boolean;
    /** La página del producto en esa tienda, para corroborar el precio. */
    url: string | null;
  }>;
  propia: { nombre: string; precio: number; url?: string | null } | null;
  /** «1 lectura es de hace más de un mes.» null si ninguna. */
  aviso: string | null;
}

/** `alProponer`: `ahora` es cuándo se congeló, y los textos lo dicen. */
export function lecturaDelMercado(
  m: MercadoDelRecorrido,
  queda: number | null,
  ahora: number = Date.now(),
  { alProponer = false }: { alProponer?: boolean } = {}
): LecturaDelMercado {
  const tiendas = [...m.tiendas].sort((a, b) => a.precio - b.precio);
  const viejas = tiendas.filter((t) => esLecturaVieja(t.leidoEn, ahora)).length;
  return {
    cabecera:
      queda != null && tiendas.length > 0 && tiendas[0].precio > 0
        ? cabecera(queda, tiendas[0].precio)
        : null,
    tiendas: tiendas.map((t, i) => ({
      nombre: t.nombre,
      precio: t.precio,
      hace: haceCuantoSeLeyo(t.leidoEn, ahora, alProponer),
      vieja: esLecturaVieja(t.leidoEn, ahora),
      frente: queda != null ? frenteA(queda, t.precio) : null,
      laMasBarata: i === 0,
      url: t.url ?? null,
    })),
    propia: m.propia,
    aviso:
      viejas === 0
        ? null
        : alProponer
          ? viejas === 1
            ? '1 lectura tenía más de un mes al proponer.'
            : `${formatoCantidad(viejas)} lecturas tenían más de un mes al proponer.`
          : viejas === 1
            ? '1 lectura es de hace más de un mes.'
            : `${formatoCantidad(viejas)} lecturas son de hace más de un mes.`,
  };
}
